// "Is this your game? Claim it or remove it" (wave 11, CONTRACTS.md "Claim / remove, nominations, unlocks"), the
// game's confirmed updates ("What's new") and the creator's nominations. Pure helpers; the pages call accounts.

import type { Game } from "./catalogue.ts";

/** The claim flow is the dashboard's (wave 2): a claim goes to a moderator. This opens it on the game's creator. */
export function claimHref(g: Pick<Game, "creator">): string {
  return `/dashboard/claim?creator=${encodeURIComponent(g.creator.id)}`;
}

export const TAKEDOWN_REASONS = ["creator_request", "copyright", "content", "broken", "other"] as const;
export type TakedownReason = (typeof TAKEDOWN_REASONS)[number];

export const TAKEDOWN_LABEL: Record<TakedownReason, string> = {
  creator_request: "It's my game and I want it off Banger",
  copyright: "It uses something I own without permission",
  content: "It breaks the content rules",
  broken: "It doesn't work any more",
  other: "Something else",
};

const EMAIL = /^[^\s@]{1,64}@[^\s@]{1,190}\.[^\s@]{2,}$/;

/**
 * The takedown body (`POST /v1/catalogue/games/:id/takedown`), or a message saying what's missing. Signed out, accounts
 * needs a `contact` email (the moderator replies there); signed in, the account is the contact.
 */
export function takedownBody(input: { reason: string; message: string; contact: string }, signedIn: boolean): { body: { reason: TakedownReason; message?: string; contact?: string } } | { error: string } {
  if (!(TAKEDOWN_REASONS as readonly string[]).includes(input.reason)) return { error: "Pick a reason." };
  const message = input.message.trim().slice(0, 2000);
  const contact = input.contact.trim();
  if (!signedIn && !EMAIL.test(contact)) return { error: "Add an email address so we can reply." };
  if (contact && !EMAIL.test(contact)) return { error: "That email address doesn't look right." };
  return { body: { reason: input.reason as TakedownReason, ...(message ? { message } : {}), ...(contact ? { contact } : {}) } };
}

export interface GameUpdate {
  versionLabel: string | null;
  source: string;
  notes: string | null;
  notesUrl: string | null;
  detectedAt: number;
}

/** accounts' `GET /v1/catalogue/games/:id/updates` → updates, dropping anything malformed. */
export function parseUpdates(raw: unknown): GameUpdate[] {
  const list = raw && typeof raw === "object" ? (raw as { updates?: unknown }).updates : null;
  if (!Array.isArray(list)) return [];
  return list
    .filter((u): u is Record<string, unknown> => !!u && typeof u === "object" && typeof (u as { detectedAt?: unknown }).detectedAt === "number")
    .map((u) => ({
      versionLabel: typeof u.versionLabel === "string" ? u.versionLabel : null,
      source: typeof u.source === "string" ? u.source : "fingerprint",
      notes: typeof u.notes === "string" && u.notes.trim() ? u.notes : null,
      notesUrl: safeLink(u.notesUrl),
      detectedAt: u.detectedAt as number,
    }));
}

/** An outside link we put in an href: http(s) only. */
export function safeLink(u: unknown): string | null {
  if (typeof u !== "string" || u.length > 500) return null;
  try {
    const url = new URL(u);
    return url.protocol === "https:" || url.protocol === "http:" ? url.href : null;
  } catch {
    return null;
  }
}

// ---- update detection (creator dashboard) -------------------------------------------------------------------------

/** Where `thrdm.json` must sit for the detector to read it: next to the listed game URL (docs/sdk/thrdm-json.md). */
export function manifestUrl(gameUrl: string): string | null {
  try {
    const u = new URL("thrdm.json", gameUrl);
    u.search = "";
    u.hash = "";
    return u.protocol === "https:" || u.protocol === "http:" ? u.href : null;
  } catch {
    return null;
  }
}

/** What the dashboard says about `dev.updateDetection`; `addManifest` asks for thrdm.json. */
export function detectionText(d: { status?: string; source?: string | null } | null | undefined): { text: string; addManifest: boolean; ok: boolean } {
  switch (d?.status) {
    case "exact":
      return { text: `We can see your updates exactly${d.source ? ` (from ${SOURCE_LABEL[d.source] ?? d.source})` : ""}, with version numbers.`, addManifest: false, ok: true };
    case "fingerprint":
      return { text: "We can tell when your game changes, but not its version or notes. Add thrdm.json to show both.", addManifest: true, ok: true };
    case "unreliable":
      return { text: "We can't detect updates for this game: its page changes on every load, so a real update looks like noise. Add thrdm.json and we'll follow that instead.", addManifest: true, ok: false };
    case "none":
      return { text: "We can't detect updates for this game yet. Add thrdm.json and we'll follow that.", addManifest: true, ok: false };
    default:
      return { text: "We haven't checked this game for updates yet.", addManifest: true, ok: false };
  }
}

export const SOURCE_LABEL: Record<string, string> = {
  hosted: "your Banger deploys",
  deploy: "your Banger deploys",
  manifest: "thrdm.json",
  feed: "your release feed",
  itch: "itch.io",
  github: "GitHub releases",
  fingerprint: "your page",
};

// ---- nominations ---------------------------------------------------------------------------------------------------

export interface Nomination {
  id: string;
  listId: string;
  status: string;
  pitch: string | null;
  createdAt: number;
}

export function parseNominations(raw: unknown): Nomination[] {
  const list = raw && typeof raw === "object" ? (raw as { nominations?: unknown }).nominations : null;
  if (!Array.isArray(list)) return [];
  return list
    .filter((n): n is Record<string, unknown> => !!n && typeof n === "object" && typeof (n as { id?: unknown }).id === "string")
    .map((n) => ({
      id: n.id as string,
      listId: typeof n.listId === "string" ? n.listId : "",
      status: typeof n.status === "string" ? n.status : "pending",
      pitch: typeof n.pitch === "string" ? n.pitch : null,
      createdAt: typeof n.createdAt === "number" ? n.createdAt : 0,
    }));
}

/** accounts' nomination errors in plain words (null: show the server's message). */
export function nominationError(code: string): string | null {
  return (
    {
      not_creator: "Only the game's creator can nominate it. Claim the listing first.",
      not_nominatable: "That row doesn't take nominations any more.",
      nomination_limit: "A game can be nominated 5 times in 30 days. Try again later.",
      season_over: "That row's season is over.",
      already_in_row: "Your game is in that row already.",
      creator_age: "Creators on Banger are 18 or older.",
    }[code] ?? null
  );
}
